import { editorUrl } from '@/lib/editorBasePath';
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Settings2 } from 'lucide-react';

interface EditTrombiDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pageId: number;
  sectionName: string;
  favColors?: string[];
  initialPrintName?: number;
  initialStyleName?: number;
  initialOrderName?: number;
  initialNameColor?: string | null;
  initialBeakName?: boolean;
  onTrombiUpdated: (data: Record<string, { objects_positions: any[] }>) => void;
  /** Called with the settings that were successfully saved, so the parent can keep them. */
  onSettingsSaved?: (settings: {
    trombi_print_name: number;
    trombi_style_name: number;
    trombi_order_name: number;
    name_color: string | null;
    beak_name: boolean;
  }) => void;
}

const PRINT_OPTIONS = [
  { label: 'Nom Prénom', value: 1 },
  { label: 'Nom', value: 2 },
  { label: 'Prénom', value: 3 },
];

const STYLE_OPTIONS = [
  { label: 'Prénom Nom', value: 1 },
  { label: 'Prénom NOM', value: 2 },
  { label: 'PRÉNOM NOM', value: 3 },
];

const ORDER_OPTIONS = [
  { label: 'Prénom Nom', value: 1 },
  { label: 'Nom Prénom', value: 2 },
];

const EditTrombiDialog: React.FC<EditTrombiDialogProps> = ({
  open,
  onOpenChange,
  pageId,
  sectionName,
  favColors = [],
  initialPrintName,
  initialStyleName,
  initialOrderName,
  initialNameColor,
  initialBeakName,
  onTrombiUpdated,
  onSettingsSaved,
}) => {
  const [printName, setPrintName] = useState(initialPrintName ?? 1);
  const [styleName, setStyleName] = useState(initialStyleName ?? 1);
  const [orderName, setOrderName] = useState(initialOrderName ?? 1);
  const [nameColor, setNameColor] = useState<string | null>(initialNameColor ?? null);
  const [beakName, setBeakName] = useState(initialBeakName ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const wasOpen = useRef(false);
  const savedRef = useRef<null | {
    trombi_print_name: number;
    trombi_style_name: number;
    trombi_order_name: number;
    name_color: string | null;
    beak_name: boolean;
  }>(null);

  useEffect(() => {
    // Only (re)initialise on the closed -> open transition, and prefer the
    // values saved during this session over the (possibly stale) props.
    if (open && !wasOpen.current) {
      const s = savedRef.current;
      setSaving(false);
      setError(null);
      setPrintName(s?.trombi_print_name ?? initialPrintName ?? 1);
      setStyleName(s?.trombi_style_name ?? initialStyleName ?? 1);
      setOrderName(s?.trombi_order_name ?? initialOrderName ?? 1);
      setNameColor(s ? s.name_color : initialNameColor ?? null);
      setBeakName(s?.beak_name ?? initialBeakName ?? false);
    }
    wasOpen.current = open;
  }, [open, initialPrintName, initialStyleName, initialOrderName, initialNameColor, initialBeakName]);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(editorUrl('/update_trombi'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page_id: pageId,
          trombi_print_name: printName,
          trombi_style_name: styleName,
          trombi_order_name: orderName,
          name_color: nameColor,
          beak_name: beakName,
        }),
      });
      if (!res.ok) throw new Error('Erreur serveur');
      const data = await res.json();
      onTrombiUpdated(data);
      const saved = {
        trombi_print_name: printName,
        trombi_style_name: styleName,
        trombi_order_name: orderName,
        name_color: nameColor,
        beak_name: beakName,
      };
      savedRef.current = saved;
      onSettingsSaved?.(saved);
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to update trombi:', err);
      setError("La modification n'a pas pu être enregistrée.");
    } finally {
      setSaving(false);
    }
  };

  const optionRow = (
    options: { label: string; value: number }[],
    current: number,
    onSelect: (v: number) => void
  ) => (
    <div className={`grid gap-2 ${options.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onSelect(opt.value)}
          className={`rounded-lg border px-2 py-2 text-xs transition-all ${
            current === opt.value
              ? 'border-primary bg-primary/10 text-foreground font-medium'
              : 'border-border text-muted-foreground hover:border-primary/40'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Settings2 size={16} /> Modifier le trombi
          </DialogTitle>
          <DialogDescription className="text-xs">{sectionName}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label className="text-xs">Affichage</Label>
            {optionRow(PRINT_OPTIONS, printName, setPrintName)}
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Style</Label>
            {optionRow(STYLE_OPTIONS, styleName, setStyleName)}
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Ordre</Label>
            {optionRow(ORDER_OPTIONS, orderName, setOrderName)}
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Couleur du nom</Label>
            {favColors.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {favColors.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setNameColor(c)}
                    title={c}
                    className={`h-7 w-7 rounded-md border-2 transition-all ${
                      nameColor === c ? 'border-primary scale-105' : 'border-border hover:border-primary/40'
                    }`}
                    style={{ backgroundColor: c }}
                  />
                ))}
                {nameColor && (
                  <button
                    type="button"
                    onClick={() => setNameColor(null)}
                    className="h-7 px-2 rounded-md border border-border text-[10px] text-muted-foreground hover:border-primary/40"
                  >
                    Aucune
                  </button>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                Aucune couleur favorite. Ajoutez-en depuis le panneau Texte.
              </p>
            )}
          </div>


          <div className="space-y-2">
            <Label className="text-xs">Mettre le nom ou prénom à la ligne</Label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Oui', value: true },
                { label: 'Non', value: false },
              ].map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => setBeakName(opt.value)}
                  className={`rounded-lg border px-2 py-2 text-xs transition-all ${
                    beakName === opt.value
                      ? 'border-primary bg-primary/10 text-foreground font-medium'
                      : 'border-border text-muted-foreground hover:border-primary/40'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuler
          </Button>
          <Button size="sm" onClick={handleSubmit} disabled={saving}>
            {saving && <Loader2 size={14} className="animate-spin mr-1" />}
            Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditTrombiDialog;
